import React, { useEffect, useMemo, useState } from "react";

import { WorkflowRow } from "./WorkflowRow";
import { getWorkflows, deleteWorkflow } from "./workflowsApi";
import { SettingsPopover } from "./recording/SettingsPopover";
import { DraftsList } from "./recording/DraftsList";
import { CopilotModalShell } from "../CopilotModalShell";
import { WorkflowEditModal } from "./WorkflowEditModal";

// Two real preconditions for recording:
//   1. IndexedDB available (false on Firefox private windows etc).
//   2. RecordingController attached (= user_id known + guac initialized).
// The chat panel mount itself is the Copilot-access gate, so we don't
// re-check that here.
function canRecord(recordingController) {
  return (
    typeof indexedDB !== "undefined" &&
    recordingController !== null &&
    recordingController !== undefined
  );
}

function whyCantRecord(recordingController) {
  // Order matters: the controller-null case is the common transient one
  // (mount races user_id / guac initialization). The IDB case only fires
  // on Firefox private windows etc.
  if (recordingController === null || recordingController === undefined) {
    return "Initializing recording — try again in a moment.";
  }
  if (typeof indexedDB === "undefined") {
    return "Recording requires local storage. Try a different browser or disable private-browsing mode.";
  }
  return "";
}

export function WorkflowLibrary({ canRun, onRunWorkflow, onClose, recordingController }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [query, setQuery] = useState("");
  // { id, mode: "edit" | "clone" } | null — drives the edit/clone modal.
  const [editing, setEditing] = useState(null);
  // workflow row pending deletion (in-app confirm), or null.
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState(false);
  // Narration preference, surfaced as an up-front checkbox on the recording
  // card. Unchecked means we skip the browser mic-permission prompt entirely
  // and take the frames-only compile path; the existing permission-denied
  // window.confirm still covers the "checked → granted? no" case.
  const [narrationOn, setNarrationOn] = useState(true);

  const confirmDelete = async () => {
    try {
      await deleteWorkflow(deleting.id);
      setDeleting(null);
      setDeleteError(false);
      fetchOnce();
    } catch (e) {
      setDeleteError(true);
    }
  };

  const fetchOnce = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getWorkflows();
      setWorkflows(rows);
    } catch (e) {
      const msg = String((e && e.message) || e);
      if (msg.includes("no_copilot_access")) {
        setError("no_access");
      } else {
        setError("generic");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOnce(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workflows;
    return workflows.filter((w) =>
      (w.name || "").toLowerCase().includes(q) ||
      (w.summary || "").toLowerCase().includes(q)
    );
  }, [workflows, query]);

  const recordable = canRecord(recordingController);
  const recordWarning = recordable ? "" : whyCantRecord(recordingController);

  return (
    <CopilotModalShell
      title="Workflows"
      onClose={onClose}
      footer={
        <button
          type="button"
          className="copilot-modal-btn copilot-modal-btn--ghost"
          onClick={onClose}
        >
          Close
        </button>
      }
    >
      <div className="copilot-workflow-library">
        <div className="cua-record-card">
          <div className="cua-record-card-title">
            <span className="cua-library-rec-dot" aria-hidden="true" />
            <span>Record a new workflow</span>
          </div>
          <p className="cua-record-card-blurb">
            Teach the AI what you want to do — open apps, click around, type —
            and Copilot will turn it into a re-runnable workflow.
          </p>
          <label className="cua-record-card-narrate">
            <input
              type="checkbox"
              checked={narrationOn}
              onChange={(e) => setNarrationOn(e.target.checked)}
              aria-describedby="cua-narrate-desc"
            />
            <span id="cua-narrate-desc">
              <strong>Narrate with your voice</strong> while you work{' '}
              <span className="cua-record-card-muted">(recommended)</span>.
              We'll use it to label the steps.
            </span>
          </label>
          <p className="cua-record-card-hint">
            When you're done, press <strong>Stop</strong> in the corner to finish.
          </p>
          <div className="cua-record-card-actions">
            <button
              type="button"
              className="cua-record-card-start"
              disabled={!recordable}
              onClick={() =>
                recordingController &&
                recordingController.startRecording({ withNarration: narrationOn })
              }
            >
              <span className="cua-library-rec-dot" aria-hidden="true" />
              Start recording
            </button>
            {recordingController && (
              <SettingsPopover controller={recordingController} />
            )}
          </div>
          {!recordable && (
            <div className="cua-library-rec-warn" role="status">
              {recordWarning}
            </div>
          )}
        </div>
        {recordingController && (
          <DraftsList controller={recordingController} />
        )}

        <input
          type="search"
          className="copilot-workflow-search"
          placeholder="Search workflows..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="copilot-workflow-list">
          {loading && <div className="copilot-modal-body">Loading…</div>}
          {!loading && error === "no_access" && (
            <div className="copilot-modal-body">You don't have Copilot access.</div>
          )}
          {!loading && error === "generic" && (
            <div className="copilot-modal-body">
              Couldn't load workflows.{" "}
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--ghost"
                onClick={fetchOnce}
              >
                Retry
              </button>
            </div>
          )}
          {!loading && !error && workflows.length === 0 && (
            <div className="copilot-modal-body">
              You don't have access to any workflows yet. Your manager can share workflows with you.
            </div>
          )}
          {!loading && !error && filtered.map((w) => (
            <WorkflowRow
              key={w.id}
              workflow={w}
              paramCount={w.parameter_count}
              canRun={canRun}
              onRun={onRunWorkflow}
              onEdit={(id) => setEditing({ id, mode: "edit" })}
              onClone={(id) => setEditing({ id, mode: "clone" })}
              onDelete={(id) => { setDeleteError(false); setDeleting(w); }}
            />
          ))}
        </div>
      </div>

      {editing && (
        <WorkflowEditModal
          workflowId={editing.id}
          mode={editing.mode}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchOnce(); }}
        />
      )}

      {deleting && (
        <CopilotModalShell
          title="Delete workflow?"
          size="sm"
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--ghost"
                onClick={() => setDeleting(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="copilot-modal-btn copilot-modal-btn--danger"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </>
          }
        >
          <p className="copilot-modal-body">
            Delete <strong>"{deleting.name}"</strong>? This can't be undone.
          </p>
          {deleteError && (
            <p className="copilot-modal-body" role="alert">
              Couldn't delete this workflow. Try again.
            </p>
          )}
        </CopilotModalShell>
      )}
    </CopilotModalShell>
  );
}
